import { FunctionComponent, useEffect, useState, useRef } from "react";
import { announcementService } from "../service/AnnouncementService";
import { Announcement, AnnouncementType, EventAnnouncement, RecurringAnnouncement, PrayerRequestAnnouncement } from "../types/announcement";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import Linkify from "react-linkify";
import Parser from "html-react-parser";
import Stack from "@mui/material/Stack";
import AddToCalendarButton from "./AddToCalendarButton";
import GetDirectionsButton from "./GetDirectionsButton";

const AnnouncementsContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(2, 0),
}));

const AnnouncementItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  backgroundColor: 'white',
  boxShadow: theme.shadows[1],
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[2],
    transform: 'translateY(-2px)',
  },
  position: 'relative',
  overflow: 'hidden',
}));

const AnnouncementHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  marginBottom: theme.spacing(2),
}));

const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  marginBottom: theme.spacing(1),
  position: 'relative',
  padding: theme.spacing(0, 10), // Leave room for absolute positioned chips
}));

const AnnouncementDate = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
}));

const AnnouncementContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  '&.truncated': {
    maxHeight: '100px',
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '40px',
      background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)',
    },
  },
}));

const SkeletonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const StyledSkeleton = styled(Skeleton)(({ theme }) => ({
  height: '100px',
  transform: 'none',
  borderRadius: theme.shape.borderRadius,
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

const UrgentBadge = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  fontWeight: 'bold',
  marginLeft: theme.spacing(1),
}));

const isValidDate = (dateString: string | null | undefined): boolean => {
  if (!dateString) return false;
  const date = new Date(dateString);
  // Check for invalid date or C# DateTime.MinValue (0001-01-01)
  return !isNaN(date.getTime()) && date.getFullYear() > 1;
};

const Announcements: FunctionComponent = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>();
  const [expandedAnnouncements, setExpandedAnnouncements] = useState<Set<string>>(new Set());
  const [overflowingAnnouncements, setOverflowingAnnouncements] = useState<Set<string>>(new Set());
  const contentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    const fetchData = async () => {
      // Using a dummy token since the component might not have access to the auth token
      // In a real app, you should get the token from your auth context
      const token = ''; // Replace with actual token from auth context
      const data = await announcementService.getAllAnnouncements(token);
      setAnnouncements(data);
    };
    fetchData();
  }, []);

  useEffect(() => {
    // Check for overflowing content after announcements are loaded
    if (announcements) {
      const newOverflowing = new Set<string>();
      announcements.forEach(announcement => {
        const contentElement = contentRefs.current[announcement.announcementId.toString()];
        if (contentElement) {
          const isOverflowing = contentElement.scrollHeight > contentElement.clientHeight;
          if (isOverflowing) {
            newOverflowing.add(announcement.announcementId.toString());
          }
        }
      });
      setOverflowingAnnouncements(newOverflowing);
    }
  }, [announcements]);

  const toggleExpand = (id: string) => {
    setExpandedAnnouncements(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const renderEventDetails = (announcement: EventAnnouncement) => {
    const hasValidTime = isValidDate(announcement.startDateTime);
    const hasLocation = !!announcement.location;

    if (!hasValidTime && !hasLocation) return null;

    return (
      <Box mt={2} mb={2} p={2} bgcolor="grey.50" borderRadius={1}>
        {hasValidTime && (
          <DetailRow>
            <AccessTimeIcon fontSize="small" />
            <Typography variant="body2">
              {new Date(announcement.startDateTime).toLocaleString()}
              {isValidDate(announcement.endDateTime) ? ` - ${new Date(announcement.endDateTime).toLocaleString()}` : ''}
            </Typography>
          </DetailRow>
        )}
        {hasLocation && (
          <DetailRow>
            <LocationOnIcon fontSize="small" />
            <Typography variant="body2">{announcement.location}</Typography>
          </DetailRow>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" mt={1.5}>
          {hasValidTime && (
            <AddToCalendarButton
              event={{
                title: announcement.title,
                description: announcement.description,
                location: announcement.location,
                startDateTime: announcement.startDateTime,
                endDateTime: isValidDate(announcement.endDateTime) ? announcement.endDateTime : undefined,
              }}
            />
          )}
          {hasLocation && <GetDirectionsButton location={announcement.location} />}
        </Stack>
      </Box>
    );
  };

  const renderRecurringEventDetails = (announcement: RecurringAnnouncement) => {
    if (!announcement.occurrences || announcement.occurrences.length === 0) return null;

    return (
      <Box mt={2} mb={2} p={2} bgcolor="grey.50" borderRadius={1}>
        <Typography variant="subtitle2" gutterBottom>Upcoming Occurrences:</Typography>
        {announcement.occurrences.slice(0, 3).map((occ) => (
          <Box key={occ.occurrenceId} mb={1}>
            <DetailRow>
              <EventIcon fontSize="small" />
              <Typography variant="body2">
                {isValidDate(occ.startDateTime) ? new Date(occ.startDateTime).toLocaleString() : 'Date TBD'}
              </Typography>
            </DetailRow>
            {isValidDate(occ.startDateTime) && (
              <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
                <AddToCalendarButton
                  event={{
                    title: announcement.title,
                    description: announcement.description,
                    startDateTime: occ.startDateTime,
                    endDateTime: isValidDate(occ.endDateTime) ? occ.endDateTime : undefined,
                  }}
                />
              </Stack>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const renderPrayerRequestDetails = (announcement: PrayerRequestAnnouncement) => (
    <Box mt={2} mb={2} p={2} bgcolor="error.50" borderRadius={1} border={1} borderColor="error.100">
      <Typography variant="subtitle2" color="error.main" gutterBottom>
        Prayer Request from {announcement.requestorName}
      </Typography>
      <Typography variant="body2">{announcement.requestDetails}</Typography>
    </Box>
  );

  const getBorderColor = (type: AnnouncementType) => {
    switch (type) {
      case AnnouncementType.Event:
        return 'primary.main';
      case AnnouncementType.RecurringEvent:
        return 'secondary.main';
      case AnnouncementType.PrayerRequest:
        return 'error.main';
      default:
        return 'transparent';
    }
  };

  return (
    <AnnouncementsContainer maxWidth="lg">
      {announcements ? (
        announcements.map((announcement) => (
          <AnnouncementItem
            key={announcement.announcementId}
            sx={{
              borderLeft: `4px solid`,
              borderLeftColor: getBorderColor(announcement.announcementType)
            }}
          >
            <AnnouncementHeader>
              <TitleContainer>
                <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center' }}>
                  {announcement.title}
                </Typography>
                <Box sx={{ position: 'absolute', right: 0, display: 'flex', gap: 1 }}>
                  {announcement.announcementType === AnnouncementType.PrayerRequest && (announcement as PrayerRequestAnnouncement).urgent && (
                    <UrgentBadge label="URGENT" size="small" icon={<PriorityHighIcon />} />
                  )}
                  {announcement.announcementType === AnnouncementType.Event && (
                    <Chip label="Event" size="small" color="primary" variant="outlined" />
                  )}
                  {announcement.announcementType === AnnouncementType.RecurringEvent && (
                    <Chip label="Recurring" size="small" color="secondary" variant="outlined" />
                  )}
                </Box>
              </TitleContainer>
              {isValidDate(announcement.publishDate) && (
                <AnnouncementDate sx={{ textAlign: 'left' }}>
                  {new Date(announcement.publishDate!).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </AnnouncementDate>
              )}
            </AnnouncementHeader>

            <AnnouncementContent
              ref={(el: HTMLDivElement | null) => { contentRefs.current[announcement.announcementId.toString()] = el; }}
              className={
                overflowingAnnouncements.has(announcement.announcementId.toString()) &&
                  !expandedAnnouncements.has(announcement.announcementId.toString())
                  ? 'truncated'
                  : ''
              }
            >
              <Linkify>
                <Typography variant="body1" component="div" style={{ whiteSpace: 'pre-wrap' }}>
                  {Parser(announcement.description)}
                </Typography>
              </Linkify>
            </AnnouncementContent>

            {announcement.announcementType === AnnouncementType.Event && renderEventDetails(announcement as EventAnnouncement)}
            {announcement.announcementType === AnnouncementType.RecurringEvent && renderRecurringEventDetails(announcement as RecurringAnnouncement)}
            {announcement.announcementType === AnnouncementType.PrayerRequest && renderPrayerRequestDetails(announcement as PrayerRequestAnnouncement)}

            {overflowingAnnouncements.has(announcement.announcementId.toString()) && (
              <Button
                size="small"
                onClick={() => toggleExpand(announcement.announcementId.toString())}
                sx={{ mt: 1 }}
              >
                {expandedAnnouncements.has(announcement.announcementId.toString()) ? 'Show Less' : 'Read More'}
              </Button>
            )}
          </AnnouncementItem>
        ))
      ) : (
        <SkeletonContainer>
          {[...Array(3)].map((_, index) => (
            <StyledSkeleton
              key={index}
              variant="rectangular"
              animation={index % 2 === 0 ? "wave" : false}
            />
          ))}
        </SkeletonContainer>
      )}
    </AnnouncementsContainer>
  );
};

export default Announcements;
